import { TextField, Typography, withStyles } from "@material-ui/core";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import AsyncButton from "../../../components/AsyncButton";
import ToggleButton from "../../../components/ToggleButton";
import UnitTextField from "../../../components/UnitTextField";
import { roundToDigits } from "../../../util/numberFormat";
import DefaultArticleLocation from "../../articles/components/DefaultArticleLocation";
import ComponentExtensionPoint from "../../plugins/ComponentExtensionPoint";
import { useRegistry } from "../../plugins/registry";

const styles = {
  method: {
    margin: "4px 0 0 -8px",
    color: "rgba(6, 5, 5, 0.54)",
  },
  flex: {
    flex: 1,
  },
  fields: {
    margin: "8px 0 16px",
    display: "flex",
    "& > *": {
      flex: 1,
    },
    "& > *:first-child:not(:last-child)": {
      marginRight: 8,
    },
    "& > *:last-child:not(:first-child)": {
      marginLeft: 8,
    },
  },
  deltaField: {
    margin: "4px 0 12px",
  },
  noteField: {
    margin: "4px 0 16px",
    width: "100%",
    "& textarea": {
      minHeight: 20, // fix scroll bar showing if empty
    },
  },
  bottom: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    "& > *": {
      flex: 1,
    },
  },
};

function DefaultStockInputField({
  className,
  method,
  label,
  autoFocus,
  InputLabelProps,
  unit,
  inputRef,
  value,
  onChange,
  onFocus,
}) {
  const handleChange = useCallback(
    (e) => {
      onChange(e.target.value, e);
    },
    [onChange],
  );

  return (
    <UnitTextField
      className={className}
      label={label}
      method={method}
      autoFocus={autoFocus}
      InputLabelProps={InputLabelProps}
      inputRef={inputRef}
      value={value}
      onChange={handleChange}
      onFocus={onFocus}
      type="number"
      unit={unit}
    />
  );
}

function CreateStockItemForm({ classes, item, onSubmit }) {
  const { t } = useTranslation();
  const registry = useRegistry();
  const stockItemType = registry.stockItemTypes[item.type];
  const { stockInfo } = stockItemType;

  const [method, setMethod] = useState("unit");
  const [delta, setDelta] = useState({
    raw: "",
    parsed: 0,
  });

  const deltaFieldRef = React.useRef(null);

  const handleDeltaInput = useCallback(
    (value) => {
      if (method === "unit") {
        if (stockInfo.allowDecimals) {
          setDelta({
            raw: value,
            parsed: roundToDigits(parseFloat(value), stockInfo.allowDecimals),
          });
        } else {
          setDelta({
            raw: value,
            parsed: parseInt(value, 10),
          });
        }
      } else {
        // weight
        let delta = null;
        const specificWeight = stockInfo.weightByStock(item, 1);
        if (!isNaN(specificWeight)) {
          if (stockInfo.allowDecimals) {
            delta = roundToDigits(
              parseFloat(value) / specificWeight,
              stockInfo.allowDecimals,
            );
          } else {
            delta = Math.round(parseFloat(value) / specificWeight);
          }
        }
        setDelta({
          raw: value,
          parsed: delta || 0,
        });
      }
    },
    [method, stockInfo, item],
  );

  const handleSetMethod = useCallback(
    (e) => {
      const newMethod = e.currentTarget.dataset.method;
      if (newMethod === method) {
        return;
      }

      setMethod(newMethod);
      if (newMethod === "unit") {
        setDelta((state) => ({
          raw: state.parsed.toString(),
          parsed: state.parsed,
        }));
      } else if (newMethod === "weight") {
        setDelta((state) => ({
          raw: roundToDigits(
            stockInfo.weightByStock(item, state.parsed),
            3,
          ).toString(),
          parsed: state.parsed,
        }));
      }
      deltaFieldRef.current?.focus();
      setTimeout(() => deltaFieldRef.current?.select());
    },
    [method, item, stockInfo],
  );

  const [location, setLocation] = useState();
  const [note, setNote] = useState("");

  const StockInput = stockInfo.StockInputField ?? DefaultStockInputField;

  return (
    <>
      <Typography variant="body1">
        {t("{{type}}-Einheit hinzufügen", {
          type: registry.stockItemTypes[item.type].displayName(1, { t }),
          context: item.type,
        })}
      </Typography>
      <Typography variant="caption">
        {[item.article.name, item.article.sku]
          .filter((text) => !!text?.trim())
          .join(" · ")}
      </Typography>
      {stockInfo.supportsDeltaByWeight(item.article) && (
        <div className={classes.method}>
          <ToggleButton
            size="small"
            value={method === "unit"}
            data-method="unit"
            onClick={handleSetMethod}
          >
            {stockInfo.deltaUnitName?.({ t }) || stockInfo.unitName({ t })}
          </ToggleButton>
          <ToggleButton
            size="small"
            value={method === "weight"}
            data-method="weight"
            onClick={handleSetMethod}
          >
            {t("Gewicht", { context: "deltaUnitName" })}
          </ToggleButton>
        </div>
      )}
      <StockInput
        className={classes.deltaField}
        label={t("Bestand")}
        fullWidth
        method={method}
        unit={
          method === "unit"
            ? stockInfo.deltaUnit?.({ t }) || stockInfo.unit({ t })
            : "kg"
        }
        inputRef={deltaFieldRef}
        value={delta.raw}
        onChange={handleDeltaInput}
        variant="standard"
        margin="none"
        autoFocus
        InputLabelProps={{ shrink: true }}
        article={item.article}
      />
      <div className={classes.fields}>
        <ComponentExtensionPoint
          name="articleFormLocation"
          variant="single"
          innerProps={{
            value: location,
            onChange: ({ compartment, loadCarrier }) => {
              setLocation({ compartment, loadCarrier });
            },
            variant: "standard",
          }}
          DefaultComponent={DefaultArticleLocation}
        />
      </div>
      <TextField
        className={classes.noteField}
        label={t("Bemerkung")}
        InputLabelProps={{ shrink: true }}
        placeholder={t("(optional)")}
        fullWidth
        multiline
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className={classes.bottom}>
        <div className={classes.flex} />
        <AsyncButton
          variant="outlined"
          color="primary"
          onClick={() => onSubmit(item.article, delta.parsed, location, note)}
          disabled={delta.parsed < 0}
        >
          {t("Hinzufügen")}
        </AsyncButton>
      </div>
    </>
  );
}

export default withStyles(styles)(CreateStockItemForm);
